import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  AnnouncementAttachment,
  AnnouncementAttachmentDownloadUrlPayload,
  AnnouncementAttachmentDownloadUrlResult,
  AnnouncementCenterListParams,
  AnnouncementCenterListResult,
  AnnouncementDetail,
  AnnouncementDispatchRecord,
  AnnouncementListParams,
  AnnouncementListResult,
  CreateAnnouncementPayload,
  RevokeAnnouncementPayload,
  UploadAnnouncementAttachmentPayload,
  UpdateAnnouncementPayload,
} from './types/announcement';

const request = createAppRequest();
const ANNOUNCEMENT_PREFIX = '/bff';

export async function getAnnouncementList(
  params?: AnnouncementListParams,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementListResult>(`${ANNOUNCEMENT_PREFIX}/announcements`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createAnnouncement(
  body: CreateAnnouncementPayload,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDetail>(`${ANNOUNCEMENT_PREFIX}/announcements`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getAnnouncementDetail(
  id: string,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDetail>(`${ANNOUNCEMENT_PREFIX}/announcements/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateAnnouncement(
  id: string,
  body: UpdateAnnouncementPayload,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDetail>(`${ANNOUNCEMENT_PREFIX}/announcements/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function revokeAnnouncement(
  id: string,
  body?: RevokeAnnouncementPayload,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDetail>(
    `${ANNOUNCEMENT_PREFIX}/announcements/${id}/revoke`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

export async function getAnnouncementDispatches(
  id: string,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDispatchRecord[]>(
    `${ANNOUNCEMENT_PREFIX}/announcements/${id}/dispatches`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getAnnouncementCenterList(
  params?: AnnouncementCenterListParams,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementCenterListResult>(
    `${ANNOUNCEMENT_PREFIX}/announcement-center/list`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getAnnouncementCenterDetail(
  id: string,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementDetail>(
    `${ANNOUNCEMENT_PREFIX}/announcement-center/${id}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function markAnnouncementAsRead(
  id: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `${ANNOUNCEMENT_PREFIX}/announcement-center/${id}/read`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function uploadAnnouncementAttachment(
  body: UploadAnnouncementAttachmentPayload,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('file', body.file);
  if (body.businessDirectory) {
    formData.append('businessDirectory', body.businessDirectory);
  }

  return request<AnnouncementAttachment>(
    `${ANNOUNCEMENT_PREFIX}/announcements/attachments/upload`,
    {
      method: 'POST',
      data: formData,
      ...(options || {}),
    },
  );
}

export async function getAnnouncementAttachmentDownloadUrl(
  params: AnnouncementAttachmentDownloadUrlPayload,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementAttachmentDownloadUrlResult>(
    `${ANNOUNCEMENT_PREFIX}/announcements/attachments/download-url`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getAnnouncementAttachmentPreviewUrl(
  params: AnnouncementAttachmentDownloadUrlPayload,
  options?: ApiRequestOptions,
) {
  return request<AnnouncementAttachmentDownloadUrlResult>(
    `${ANNOUNCEMENT_PREFIX}/announcements/attachments/preview-url`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}
